import type { IntakeFinding, RfqRecord, RfqStats, RfqStatus } from "./rfqApi";
import { rfqDisplayStatus } from "./rfqEvidence";
import { FOLLOW_UP_AFTER_DAYS, daysSince } from "./rfqJourney";

const ANSWERED = new Set<RfqStatus>(["ready", "sent", "waiting_customer", "won", "lost"]);

const DAY = 86_400_000;

export type WeekPoint = {
  label: string;
  received: number;
  answered: number;
};

export type QuoteWindow = {
  received: number;
  answered: number;
  quotedTry: number;
  medianHours: number | null;
  weeks: WeekPoint[];
};

function startOfDay(now: Date): number {
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

function dayLabel(at: number, locale: string, today: boolean): string {
  if (today) return locale === "tr" ? "Bugün" : "Today";
  try {
    return new Date(at).toLocaleDateString(locale === "tr" ? "tr-TR" : "en-GB", {
      day: "numeric",
      month: "short",
    });
  } catch {
    return "";
  }
}

/** Last 28 days of RFQs, for the home value band. Empty input stays at zero. */
export function quoteWindow(
  rfqs: RfqRecord[],
  opts: { now?: Date; locale?: string } = {},
): QuoteWindow {
  const now = opts.now ?? new Date();
  const locale = opts.locale ?? "en";
  const origin = startOfDay(now) - 28 * DAY;
  const weekMs = 7 * DAY;
  const weeks: WeekPoint[] = [0, 1, 2, 3, 4].map((i) => ({
    label: dayLabel(origin + i * weekMs, locale, i === 4),
    received: 0,
    answered: 0,
  }));

  let received = 0;
  let answered = 0;
  let quotedTry = 0;
  const durations: number[] = [];
  const weekAgo = now.getTime() - weekMs;

  for (const rfq of rfqs) {
    const created = Date.parse(rfq.created_at ?? "");
    if (!Number.isNaN(created) && created >= origin && created <= now.getTime()) {
      const idx = Math.min(4, Math.floor((created - origin) / weekMs));
      weeks[idx].received += 1;
      if (ANSWERED.has(rfq.status)) weeks[idx].answered += 1;
    }
    if (!Number.isNaN(created) && created >= weekAgo) {
      received += 1;
      if (ANSWERED.has(rfq.status)) {
        answered += 1;
        const end = Date.parse(rfq.sent_at ?? rfq.updated_at ?? "");
        if (!Number.isNaN(end) && end >= created) durations.push((end - created) / 3_600_000);
      }
    }
    if (
      ANSWERED.has(rfq.status) &&
      typeof rfq.quote_total_try === "number" &&
      !Number.isNaN(created) &&
      created >= weekAgo
    ) {
      quotedTry += rfq.quote_total_try;
    }
  }

  durations.sort((a, b) => a - b);
  const medianHours =
    durations.length === 0
      ? null
      : durations.length % 2 === 1
        ? durations[(durations.length - 1) / 2]
        : (durations[durations.length / 2 - 1] + durations[durations.length / 2]) / 2;

  return { received, answered, quotedTry, medianHours, weeks };
}

export function formatDuration(hours: number): string {
  const whole = Math.floor(hours);
  const minutes = Math.round((hours - whole) * 60);
  if (whole <= 0) return `${minutes} min`;
  if (minutes === 0) return `${whole} h`;
  return `${whole} h ${minutes} min`;
}

/** Four home-row segments: Uploaded, BOM, Costed, Quoted. */
export type HomeStepState = "done" | "next" | "todo";

export type HomeRowAction = "review-bom" | "start-costing" | "open-quote";

export type HomeRowTone = "uploaded" | "approved" | "ready" | "closed";

export type HomeNext = "bom" | "costing" | "quote" | "margin";

export type HomeRowProgress = {
  steps: [HomeStepState, HomeStepState, HomeStepState, HomeStepState];
  /** What the "Next" label names. Null once the quote has left the desk. */
  next: HomeNext | null;
  action: HomeRowAction;
  tone: HomeRowTone;
};

/**
 * Progress for one Recent RFQs row.
 *
 * The four steps follow the status the list already carries, except a
 * quote-like status with no file and no parsed drawing. That record is shown
 * as Uploaded — the same place as a draft — so it can never read Quoted.
 * See `rfqDisplayStatus`.
 *
 * Lines refine "BOM approved" into costed-or-not only when they are present.
 * An estimating RFQ with no lines stays on Start costing — the list often
 * omits line costs, and a missing cost is not a finished one.
 */
export function homeRowProgress(rfq: RfqRecord): HomeRowProgress {
  const status = rfqDisplayStatus(rfq);
  const closed = status === "won" || status === "lost" || status === "cancelled";
  const quoted =
    closed || status === "sent" || status === "ready" || status === "waiting_customer";
  if (quoted) {
    const needsMargin =
      (status === "ready" || status === "waiting_customer") && rfq.approved_margin_pct == null;
    return {
      steps: ["done", "done", "done", "done"],
      next: needsMargin ? "margin" : null,
      action: "open-quote",
      tone: closed ? "closed" : "ready",
    };
  }
  if (status === "estimating") {
    const lines = rfq.lines ?? [];
    const costed = lines.length > 0 && lines.every((line) => typeof line.est_cost_try === "number");
    if (costed) {
      return {
        steps: ["done", "done", "done", "next"],
        next: "quote",
        action: "open-quote",
        tone: "approved",
      };
    }
    return {
      steps: ["done", "done", "next", "todo"],
      next: "costing",
      action: "start-costing",
      tone: "approved",
    };
  }
  return {
    steps: ["done", "next", "todo", "todo"],
    next: "bom",
    action: "review-bom",
    tone: "uploaded",
  };
}

const OPEN_STATUS = new Set<RfqStatus>(["draft", "estimating", "waiting_customer", "ready", "sent"]);

/**
 * Same formula as engine `rfq_stats` / `win_rate`: won / (won + lost),
 * cancelled left out, null when nothing has been decided.
 *
 * Demo mode has no engine, so the home board derives this from the fixture
 * RFQs. A live session must call `GET /v1/rfqs/stats` instead — the list is
 * capped and would undercount.
 */
export function orgStatsFromRfqs(rfqs: RfqRecord[]): RfqStats {
  let won = 0;
  let lost = 0;
  let cancelled = 0;
  let open = 0;
  for (const rfq of rfqs) {
    if (rfq.status === "won") won += 1;
    else if (rfq.status === "lost") lost += 1;
    else if (rfq.status === "cancelled") cancelled += 1;
    else if (OPEN_STATUS.has(rfq.status)) open += 1;
  }
  const decided = won + lost;
  return {
    won,
    lost,
    cancelled,
    open,
    win_rate: decided > 0 ? Math.round((won / decided) * 10_000) / 10_000 : null,
  };
}

/** Hide the win-rate card unless the payload is the engine's stats object. */
export function isRfqStats(value: unknown): value is RfqStats {
  if (!value || typeof value !== "object") return false;
  const row = value as Record<string, unknown>;
  for (const key of ["won", "lost", "cancelled", "open"] as const) {
    if (typeof row[key] !== "number" || !Number.isFinite(row[key])) return false;
  }
  return row.win_rate === null || (typeof row.win_rate === "number" && Number.isFinite(row.win_rate));
}

export type SentWonPoint = {
  label: string;
  sent: number;
  won: number;
};

/**
 * Sent vs won over the last 28 days, five weekly buckets ending today.
 *
 * `GET /v1/rfqs/stats` is an all-time total. It has no week series, so the
 * chart counts `sent_at` and, for won quotes, `outcome_at` (else `updated_at`)
 * on the RFQ list. A week with nothing in it stays at zero.
 */
export function sentWonSeries(
  rfqs: RfqRecord[],
  opts: { now?: Date; locale?: string } = {},
): SentWonPoint[] {
  const now = opts.now ?? new Date();
  const locale = opts.locale ?? "en";
  const origin = startOfDay(now) - 28 * DAY;
  const weekMs = 7 * DAY;
  const points: SentWonPoint[] = [0, 1, 2, 3, 4].map((i) => ({
    label: dayLabel(origin + i * weekMs, locale, i === 4),
    sent: 0,
    won: 0,
  }));
  const end = now.getTime();

  for (const rfq of rfqs) {
    const sentAt = Date.parse(rfq.sent_at ?? "");
    if (!Number.isNaN(sentAt) && sentAt >= origin && sentAt <= end) {
      const idx = Math.min(4, Math.floor((sentAt - origin) / weekMs));
      points[idx].sent += 1;
    }
    if (rfq.status !== "won") continue;
    const wonAt = Date.parse(rfq.outcome_at ?? rfq.updated_at ?? "");
    if (!Number.isNaN(wonAt) && wonAt >= origin && wonAt <= end) {
      const idx = Math.min(4, Math.floor((wonAt - origin) / weekMs));
      points[idx].won += 1;
    }
  }
  return points;
}

export type HomeWaitKind = "missing" | "customer" | "followup" | "margin";

export type HomeWaitItem = {
  kind: HomeWaitKind;
  rfq: RfqRecord;
  /** Parts with no stock match. Set on missing rows. */
  missing?: number;
  /**
   * Customer-answerable findings. Null when the record has no findings list
   * (the RFQ list omits them) so the UI can leave the count off.
   */
  questions?: number | null;
  /** Whole days since `sent_at`. Set on follow-up rows. */
  days?: number;
  /** `suggested_margin_pct`. Set on margin rows. */
  marginPct?: number;
};

function recency(rfq: RfqRecord): number {
  return Date.parse(rfq.updated_at ?? rfq.created_at ?? "") || 0;
}

function newest(rows: RfqRecord[]): RfqRecord | null {
  if (rows.length === 0) return null;
  return rows.reduce((best, row) => (recency(row) > recency(best) ? row : best));
}

/** Questions the customer can answer. Internal gaps are not counted. */
export function customerQuestionCount(findings: IntakeFinding[] | null | undefined): number | null {
  if (!Array.isArray(findings)) return null;
  return findings.filter(
    (finding) =>
      finding.field === "qty" ||
      finding.field === "due_at" ||
      finding.kind === "unread" ||
      finding.kind === "failed",
  ).length;
}

function repliedAfterSend(rfq: RfqRecord): boolean {
  if (!Array.isArray(rfq.customer_replies) || !rfq.sent_at) return false;
  const sent = Date.parse(rfq.sent_at);
  if (Number.isNaN(sent)) return false;
  return rfq.customer_replies.some((reply) => {
    const at = Date.parse(reply.created_at ?? "");
    return !Number.isNaN(at) && at >= sent;
  });
}

/**
 * One row per kind, in the home priority order.
 *
 * Each row is one RFQ the list can already describe. A kind with nothing
 * behind it is omitted — the card never invents a task.
 */
export function homeWaiting(rfqs: RfqRecord[], now = new Date()): HomeWaitItem[] {
  const items: HomeWaitItem[] = [];
  const missing = rfqs.filter((rfq) => (rfq.stock_missing_count ?? 0) > 0);
  if (missing.length > 0) {
    const rfq = missing.reduce((best, row) => {
      const gap = (row.stock_missing_count ?? 0) - (best.stock_missing_count ?? 0);
      if (gap !== 0) return gap > 0 ? row : best;
      return recency(row) > recency(best) ? row : best;
    });
    items.push({ kind: "missing", rfq, missing: rfq.stock_missing_count ?? 0 });
  }

  const customer = newest(rfqs.filter((rfq) => rfq.status === "waiting_customer"));
  if (customer) {
    items.push({
      kind: "customer",
      rfq: customer,
      questions: customerQuestionCount(customer.findings),
    });
  }

  const quiet = rfqs.filter((rfq) => {
    if (rfq.status !== "sent" || repliedAfterSend(rfq)) return false;
    const days = daysSince(rfq.sent_at, now.getTime());
    return days != null && days >= FOLLOW_UP_AFTER_DAYS;
  });
  const follow = newest(quiet);
  if (follow) {
    items.push({
      kind: "followup",
      rfq: follow,
      days: daysSince(follow.sent_at, now.getTime()) ?? FOLLOW_UP_AFTER_DAYS,
    });
  }

  const margin = newest(
    rfqs.filter(
      (rfq) =>
        (rfq.status === "estimating" || rfq.status === "ready") &&
        rfq.approved_margin_pct == null &&
        typeof rfq.suggested_margin_pct === "number" &&
        typeof rfq.est_cost_try === "number",
    ),
  );
  if (margin && typeof margin.suggested_margin_pct === "number") {
    items.push({ kind: "margin", rfq: margin, marginPct: margin.suggested_margin_pct });
  }
  return items;
}

/** "14 min" / "1 h" / "Yesterday" from a real timestamp. */
export function homeAgeLabel(
  iso: string | null | undefined,
  opts: { now?: Date; locale?: string } = {},
): string | null {
  if (!iso) return null;
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return null;
  const now = opts.now ?? new Date();
  const tr = opts.locale === "tr";
  const delta = Math.max(0, now.getTime() - at);
  const minutes = Math.round(delta / 60_000);
  if (minutes < 60) return tr ? `${minutes} dk` : `${minutes} min`;
  const hours = Math.round(delta / 3_600_000);
  if (hours < 24) return tr ? `${hours} sa` : `${hours} h`;
  const startToday = startOfDay(now);
  const startAt = startOfDay(new Date(at));
  const dayDiff = Math.round((startToday - startAt) / DAY);
  if (dayDiff === 1) return tr ? "Dün" : "Yesterday";
  try {
    return new Date(at).toLocaleDateString(tr ? "tr-TR" : "en-GB", { day: "numeric", month: "short" });
  } catch {
    return null;
  }
}

/** "today 10:05" or a short date, for the customer-waiting row. */
export function homeSentWhen(
  iso: string | null | undefined,
  opts: { now?: Date; locale?: string } = {},
): string | null {
  if (!iso) return null;
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return null;
  const now = opts.now ?? new Date();
  const tr = opts.locale === "tr";
  let time = "";
  try {
    time = new Date(at).toLocaleTimeString(tr ? "tr-TR" : "en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });
  } catch {
    time = "";
  }
  if (new Date(at).toDateString() === now.toDateString()) {
    return tr ? `bugün ${time}`.trim() : `today ${time}`.trim();
  }
  try {
    const date = new Date(at).toLocaleDateString(tr ? "tr-TR" : "en-GB", { day: "numeric", month: "short" });
    return `${date} ${time}`.trim();
  } catch {
    return time || null;
  }
}
